import { isAxiosError } from 'axios'
import { Loader2, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useProductSourceChunks,
  useProductSourceDownloadUrl,
  type ProductSource,
} from '@/shared/hooks/api/productSource'
import { getProductSourcePreviewMode } from '../utils/isRagIngestedProductSource'
import { ProductSourceChunksWithPdf } from './ProductSourceChunksWithPdf'

export function ProductSourceInspectSection({
  productId,
  source,
  enabled,
}: {
  productId: string
  source: ProductSource
  enabled: boolean
}) {
  const { t } = useTranslation('productCatalog')

  const isIngestionCompleted = source.ingestionStatus === 'COMPLETED'

  const {
    data: chunksData,
    isLoading: isChunksLoading,
    isError: isChunksError,
    error: chunksError,
  } = useProductSourceChunks(productId, source.id, {
    enabled: enabled && isIngestionCompleted,
  })

  const filePathInBucket =
    isIngestionCompleted && source.filePathInBucket
      ? source.filePathInBucket
      : undefined

  const { data: downloadUrlData } = useProductSourceDownloadUrl(
    productId,
    enabled && isIngestionCompleted ? filePathInBucket : undefined
  )

  const showNotIndexed = !isIngestionCompleted
  const showChunksLoading = isIngestionCompleted && isChunksLoading
  const showChunksError = isIngestionCompleted && isChunksError
  const showEmptyChunks =
    isIngestionCompleted &&
    !isChunksLoading &&
    !isChunksError &&
    (chunksData?.chunks.length ?? 0) === 0
  const showChunksList =
    isIngestionCompleted &&
    !isChunksLoading &&
    !isChunksError &&
    (chunksData?.chunks.length ?? 0) > 0

  const isRagUnavailable =
    isChunksError &&
    isAxiosError(chunksError) &&
    chunksError.response?.status === 424

  let chunksErrorMessage = t('sources.inspect.loadError')
  if (isRagUnavailable) {
    chunksErrorMessage = t('sources.inspect.ragUnavailable')
  }

  return (
    <section className='space-y-4 border-t pt-5'>
      <div className='space-y-1.5'>
        <div className='flex items-center gap-2'>
          <Search className='size-4 shrink-0 text-muted-foreground' aria-hidden />
          <h3 className='text-sm font-medium'>{t('sources.inspect.sectionTitle')}</h3>
        </div>
        <p className='text-sm text-muted-foreground'>
          {t('sources.inspect.sectionDescription')}
        </p>
      </div>

      {showNotIndexed && (
        <p className='text-sm text-muted-foreground'>
          {t('sources.inspect.notIndexed')}
        </p>
      )}

      {showChunksLoading && (
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='size-6 animate-spin text-muted-foreground' />
        </div>
      )}

      {showChunksError && (
        <p className='text-sm text-muted-foreground'>{chunksErrorMessage}</p>
      )}

      {showEmptyChunks && (
        <p className='text-sm text-muted-foreground'>
          {t('sources.inspect.emptyChunks')}
        </p>
      )}

      {showChunksList && chunksData != null && (
        <ProductSourceChunksWithPdf
          chunks={chunksData.chunks}
          downloadUrl={downloadUrlData?.url}
          previewMode={getProductSourcePreviewMode(source.type)}
        />
      )}
    </section>
  )
}
